"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LOCKED_CELL_REASON = exports.RESTRICTED_TRANSITION_RULES = exports.RESTRICTED_TRANSITIONS = exports.RESTRICTED_FIELD_PLACEHOLDER = exports.FIELD_RESTRICTIONS_BY_FIELD = exports.RESTRICTED_ENTITIES = exports.FIELD_RESTRICTION_OBJECTS = exports.FIELD_RESTRICTION_TARGETS = exports.FIELD_RESTRICTIONS = exports.PERMISSION_MATRIX = exports.PERMISSION_OBJECT_LABELS = exports.PERMISSION_OBJECTS = void 0;
exports.fieldRestrictionLabel = fieldRestrictionLabel;
exports.cellFor = cellFor;
exports.isLockedCell = isLockedCell;
exports.canRead = canRead;
exports.canWrite = canWrite;
exports.canSeeField = canSeeField;
exports.canPerformRestrictedTransition = canPerformRestrictedTransition;
exports.hiddenFieldsFor = hiddenFieldsFor;
exports.isRoleKey = isRoleKey;
exports.restrictedFieldsFor = restrictedFieldsFor;
exports.maskedPropertiesFor = maskedPropertiesFor;
const enums_1 = require("./enums");
// §L23 permission matrix, verbatim, plus the field-level restrictions and the
// transition permissions.
//
// Non-negotiable #5: field-level permissions are enforced in the *serialisation*
// layer, so they cover detail screens, list views, exports and future APIs
// automatically. A restricted field renders as an em dash, never silently omitted.
/** Every object the matrix has a row for. */
exports.PERMISSION_OBJECTS = [
    'org_unit_position',
    'headcount_plan',
    'requisition',
    'approval_action',
    'job_opening',
    'job_config',
    'posting',
    'candidate',
    'application',
    'screening_decision',
    'assessment_template',
    'assessment_attempt',
    'exam_session',
    'interview',
    'scorecard',
    'stage_decision',
    'offer',
    'talent_pool',
    'activity_message',
    'reports_own',
    'reports_all_staff',
    'admin_config',
    'hiring_setup',
    'user_management',
    'audit_log',
];
exports.PERMISSION_OBJECT_LABELS = {
    org_unit_position: 'Org unit / position',
    headcount_plan: 'Headcount plan',
    requisition: 'Requisition',
    approval_action: 'Approval action',
    job_opening: 'Job opening',
    job_config: 'Job config / interview plan',
    posting: 'Posting',
    candidate: 'Candidate',
    application: 'Application',
    screening_decision: 'Screening decision',
    assessment_template: 'Assessment template / bank',
    assessment_attempt: 'Assessment attempt',
    exam_session: 'Exam session',
    interview: 'Interview',
    scorecard: 'Scorecard',
    stage_decision: 'Stage decision',
    offer: 'Offer',
    talent_pool: 'Talent pool',
    activity_message: 'Activity / message',
    reports_own: 'Reports — own',
    reports_all_staff: 'Reports — all staff',
    admin_config: 'Admin config',
    hiring_setup: 'Hiring set-up',
    user_management: 'User management',
    audit_log: 'Audit log',
};
const NONE = { level: 'none' };
const FULL = { level: 'full' };
const READ = { level: 'read', readOnly: true };
/** Candidates hold no internal permissions — D16 removed the portal from V1 entirely. */
const CANDIDATE_ROW = NONE;
exports.PERMISSION_MATRIX = {
    org_unit_position: {
        admin: FULL,
        hr_lead: FULL,
        leadership: READ,
        recruiter: READ,
        hiring_manager: { level: 'scoped', scope: 'own_org_unit', readOnly: true },
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    headcount_plan: {
        admin: FULL,
        hr_lead: FULL,
        leadership: READ,
        recruiter: READ,
        hiring_manager: { level: 'scoped', scope: 'own_org_unit', readOnly: true },
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    requisition: {
        admin: FULL,
        hr_lead: FULL,
        leadership: READ,
        recruiter: { level: 'scoped', scope: 'own_records' },
        hiring_manager: { level: 'scoped', scope: 'own_requests' },
        panelist: NONE,
        approver: { level: 'scoped', scope: 'assigned_only' },
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    approval_action: {
        admin: FULL,
        hr_lead: FULL,
        leadership: { level: 'scoped', scope: 'assigned_only' },
        recruiter: NONE,
        hiring_manager: { level: 'scoped', scope: 'assigned_only' },
        panelist: NONE,
        approver: { level: 'scoped', scope: 'assigned_only' },
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    job_opening: {
        admin: FULL,
        hr_lead: FULL,
        leadership: READ,
        recruiter: { level: 'scoped', scope: 'own_records' },
        hiring_manager: { level: 'scoped', scope: 'own_department', readOnly: true },
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        // ADR 0085: opens the jobs in their area to reach each one's Contact stage; changes nothing.
        hr_assistant: { level: 'scoped', scope: 'own_records', readOnly: true },
        candidate: CANDIDATE_ROW,
    },
    job_config: {
        admin: FULL,
        hr_lead: FULL,
        leadership: READ,
        recruiter: { level: 'scoped', scope: 'own_records' },
        hiring_manager: NONE,
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    posting: {
        admin: FULL,
        hr_lead: FULL,
        leadership: READ,
        recruiter: { level: 'scoped', scope: 'own_records' },
        hiring_manager: NONE,
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    candidate: {
        admin: FULL,
        hr_lead: FULL,
        leadership: READ,
        recruiter: FULL,
        // Redesign Group 1 (DECISIONS-LOG 2026-10-18 Q1 (a) "Own jobs only"): the profile of someone
        // who applied to one of their jobs, read only. Never the list, search or counts — those need
        // full or read (`seesAllCandidates`). Contact, salary and NID stay masked by the field rules.
        hiring_manager: { level: 'scoped', scope: 'own_jobs', readOnly: true },
        panelist: { level: 'scoped', scope: 'interviewees_only' },
        approver: NONE,
        invigilator: NONE,
        // ADR 0085: only so a candidate's phone and email show where they contact or greet them.
        // Never the list, search or profile (`seesAllCandidates` and `mayEditCandidates` stay false).
        hr_assistant: { level: 'scoped', scope: 'contact_details_only', readOnly: true },
        candidate: CANDIDATE_ROW,
    },
    application: {
        admin: FULL,
        hr_lead: FULL,
        leadership: READ,
        recruiter: { level: 'scoped', scope: 'own_records' },
        hiring_manager: { level: 'scoped', scope: 'own_jobs', readOnly: true },
        panelist: { level: 'scoped', scope: 'interviewees_only', readOnly: true },
        approver: NONE,
        invigilator: NONE,
        // ADR 0085: reads a job's people to work its contact list. Moves, imports and rankings need write.
        hr_assistant: { level: 'scoped', scope: 'own_records', readOnly: true },
        candidate: CANDIDATE_ROW,
    },
    screening_decision: {
        admin: FULL,
        hr_lead: FULL,
        leadership: READ,
        recruiter: { level: 'scoped', scope: 'own_records' },
        hiring_manager: NONE,
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    assessment_template: {
        admin: FULL,
        hr_lead: FULL,
        leadership: NONE,
        recruiter: { level: 'scoped', scope: 'own_records' },
        hiring_manager: NONE,
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    assessment_attempt: {
        admin: FULL,
        hr_lead: FULL,
        leadership: NONE,
        recruiter: { level: 'scoped', scope: 'own_records' },
        hiring_manager: { level: 'scoped', scope: 'own_jobs', readOnly: true },
        panelist: { level: 'read', scope: 'interviewees_only', readOnly: true },
        // §L23 printed this row one column to the right, giving approvers "own session";
        // corrected in S18 (DECISIONS-LOG S18 Q8) — approvers never see test attempts.
        approver: NONE,
        invigilator: { level: 'scoped', scope: 'own_session' },
        hr_assistant: { level: 'scoped', scope: 'own_session' },
        candidate: CANDIDATE_ROW,
    },
    exam_session: {
        admin: FULL,
        hr_lead: FULL,
        leadership: NONE,
        recruiter: { level: 'scoped', scope: 'own_records' },
        hiring_manager: NONE,
        panelist: NONE,
        approver: NONE,
        invigilator: { level: 'full', scope: 'own_only' },
        // ADR 0085: watches the tests taken in the office, as an invigilator does.
        hr_assistant: { level: 'full', scope: 'own_only' },
        candidate: CANDIDATE_ROW,
    },
    interview: {
        admin: FULL,
        hr_lead: FULL,
        leadership: READ,
        recruiter: { level: 'scoped', scope: 'own_records' },
        hiring_manager: { level: 'scoped', scope: 'own_jobs', readOnly: true },
        panelist: { level: 'scoped', scope: 'own_only' },
        approver: NONE,
        invigilator: NONE,
        // ADR 0085: today's interviews, to greet people and have the room ready; read only.
        hr_assistant: { level: 'scoped', scope: 'own_records', readOnly: true },
        candidate: CANDIDATE_ROW,
    },
    scorecard: {
        admin: FULL,
        hr_lead: FULL,
        leadership: READ,
        recruiter: { level: 'scoped', scope: 'own_records', readOnly: true },
        hiring_manager: { level: 'scoped', scope: 'own_rounds' },
        panelist: { level: 'scoped', scope: 'own_only' },
        approver: NONE,
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    stage_decision: {
        admin: FULL,
        hr_lead: FULL,
        leadership: READ,
        recruiter: { level: 'scoped', scope: 'own_records' },
        hiring_manager: { level: 'scoped', scope: 'own_rounds' },
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    offer: {
        admin: FULL,
        hr_lead: FULL,
        leadership: { level: 'scoped', scope: 'assigned_only' },
        recruiter: { level: 'scoped', scope: 'own_records' },
        hiring_manager: {
            level: 'scoped',
            scope: 'own_jobs',
            readOnly: true,
            // §L23: "S (R, no amounts)". A field PATH, not a column list — the properties
            // it covers live in FIELD_RESTRICTION_TARGETS, in one place.
            hiddenFields: ['offer.amount'],
        },
        panelist: NONE,
        approver: { level: 'scoped', scope: 'assigned_only' },
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    talent_pool: {
        admin: FULL,
        hr_lead: FULL,
        leadership: READ,
        recruiter: FULL,
        hiring_manager: NONE,
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    activity_message: {
        admin: FULL,
        hr_lead: FULL,
        leadership: READ,
        recruiter: { level: 'scoped', scope: 'own_records' },
        hiring_manager: { level: 'scoped', scope: 'own_jobs', readOnly: true },
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        // ADR 0085: logs calls, sends the templated contact emails and marks contact status.
        hr_assistant: { level: 'scoped', scope: 'own_records' },
        candidate: CANDIDATE_ROW,
    },
    reports_own: {
        admin: FULL,
        hr_lead: FULL,
        leadership: FULL,
        recruiter: FULL,
        hiring_manager: { level: 'scoped', scope: 'own_records' },
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    reports_all_staff: {
        admin: FULL,
        hr_lead: FULL,
        leadership: FULL,
        recruiter: NONE,
        hiring_manager: NONE,
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    admin_config: {
        // ADR 0073: the Admin area is Admin only.
        admin: FULL,
        hr_lead: NONE,
        leadership: NONE,
        recruiter: NONE,
        hiring_manager: NONE,
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    hiring_setup: {
        // ADR 0073: the Admin pages HR keeps (reasons, message templates, pipelines, rubrics,
        // interview kits and plans, role templates, pools and tags, sources, offer letters,
        // career site, calendars, rooms and the staff list).
        admin: FULL,
        hr_lead: FULL,
        leadership: NONE,
        recruiter: NONE,
        hiring_manager: NONE,
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    user_management: {
        // ADR 0073: the Admin area is Admin only.
        admin: FULL,
        hr_lead: NONE,
        leadership: NONE,
        recruiter: NONE,
        hiring_manager: NONE,
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
    audit_log: {
        // ADR 0073: the Admin area is Admin only.
        admin: FULL,
        hr_lead: NONE,
        leadership: NONE,
        recruiter: NONE,
        hiring_manager: NONE,
        panelist: NONE,
        approver: NONE,
        invigilator: NONE,
        hr_assistant: NONE,
        candidate: CANDIDATE_ROW,
    },
};
/**
 * The serialisation layer consults this on every surface. A field absent from a
 * viewer's set renders as an em dash — never dropped from the payload shape, so a
 * list column stays aligned and an export keeps its header.
 */
exports.FIELD_RESTRICTIONS = [
    {
        field: 'position.salaryBand',
        label: 'Position salary band',
        visibleTo: ['admin', 'hr_lead', 'leadership', 'recruiter', 'approver'],
        note: 'Approver only on assigned requisitions.',
    },
    {
        field: 'candidate.expectedSalary',
        label: 'Candidate expected salary',
        visibleTo: ['admin', 'hr_lead', 'leadership', 'recruiter'],
    },
    {
        field: 'offer.amount',
        label: 'Offer amount and breakdown',
        visibleTo: ['admin', 'hr_lead', 'leadership', 'recruiter', 'approver'],
        note: 'Approver only when assigned.',
    },
    {
        field: 'candidate.currentSalary',
        label: "Internal applicant's current salary",
        visibleTo: ['admin', 'hr_lead'],
    },
    {
        field: 'candidate.doNotRehireReason',
        label: 'Do-not-rehire reason',
        visibleTo: ['admin', 'hr_lead'],
    },
    {
        field: 'candidate.nationalId',
        label: 'NID and personal identity documents',
        visibleTo: ['admin', 'hr_lead', 'recruiter'],
    },
    {
        field: 'candidate.contactDetails',
        label: 'Candidate contact details',
        // ADR 0085: the HR assistant calls and emails the people on a job's contact list.
        visibleTo: ['admin', 'hr_lead', 'recruiter', 'hr_assistant'],
        note: 'Interviewers see the name only.',
    },
    {
        field: 'report.recruiterProficiency',
        label: 'Recruiter proficiency metrics',
        visibleTo: ['admin', 'hr_lead', 'leadership', 'recruiter'],
        note: "Recruiters see only their own.",
    },
    {
        field: 'application.rejectionReason',
        label: 'Rejection reasons',
        visibleTo: [
            'admin',
            'hr_lead',
            'leadership',
            'recruiter',
            'hiring_manager',
            'panelist',
            'approver',
            'invigilator',
            'hr_assistant',
        ],
        note: 'All internal roles; never candidates.',
    },
    {
        // R4 (§L29 "Kept, not thrown away"): an export's other columns can hold anything a form asked.
        field: 'application.importedDetails',
        label: 'Imported details: CSV columns not matched to a field',
        visibleTo: ['admin', 'hr_lead', 'recruiter'],
    },
];
exports.FIELD_RESTRICTION_TARGETS = {
    'position.salaryBand': {
        entity: 'position',
        properties: ['salaryBandMinMinor', 'salaryBandMaxMinor', 'salaryCurrency', 'salaryBand'],
        also: [
            // Owner, 2026-10-18 Q2: a job's optional salary budget (JobDetail.salaryBudget).
            { entity: 'job_budget', properties: ['minMinor', 'maxMinor', 'currency'] },
            // Review W2 (2026-11-02): the same budget wherever else it travels — an application row carries its job's budget, and
            // "N% over budget" or "within budget" gives the budget away to anyone who can see the ask. As names, these also hide
            // the budget in the event log's recorded data (`job.draft_saved` records `budgetMinMinor`/`budgetMaxMinor`).
            { entity: 'application', properties: ['budgetMinMinor', 'budgetMaxMinor', 'budgetCurrency', 'expectedSalaryOverBudgetPercent', 'expectedSalaryWithinBudget'] },
            { entity: 'candidate', properties: ['expectedSalaryOverBudgetPercent'] },
        ],
    },
    'candidate.expectedSalary': {
        entity: 'candidate',
        properties: ['expectedSalaryMinor', 'expectedSalaryCurrency', 'expectedSalary', 'expectedSalaryOverBudgetPercent'],
        // CandidateApplicationRow: this application's own ask, its job's budget and how far over it is.
        also: [
            {
                entity: 'application',
                properties: ['expectedSalaryMinor', 'expectedSalaryCurrency', 'budgetMinMinor', 'budgetMaxMinor', 'budgetCurrency', 'expectedSalaryOverBudgetPercent', 'expectedSalaryWithinBudget'],
            },
        ],
    },
    'offer.amount': {
        entity: 'offer',
        properties: [
            'baseSalaryMinor',
            'currency',
            'allowances',
            'totalAnnualMinor',
            'isOutOfBand',
            'breakdown',
            // S23: the gross total, the band it is judged against, and the letter, which
            // states the salary in words.
            'grossMonthlyMinor',
            'bandMinMinor',
            'bandMaxMinor',
            'bandPosition',
            'letterText',
        ],
    },
    'candidate.currentSalary': {
        entity: 'candidate',
        properties: ['currentSalaryMinor', 'currentSalaryCurrency', 'currentSalary'],
    },
    'candidate.doNotRehireReason': {
        // The flag itself stays visible — a recruiter must know not to proceed. Only the
        // reason is HR-Lead-only (§L23), because that is where the sensitive detail is.
        entity: 'candidate',
        properties: ['doNotRehireReason'],
    },
    'candidate.nationalId': {
        entity: 'candidate',
        properties: ['nationalId', 'dateOfBirth', 'identityDocuments'],
    },
    'candidate.contactDetails': {
        // §L23: "panelists see name only". The name is never in this list.
        entity: 'candidate',
        properties: ['email', 'phone', 'addressLine', 'city', 'linkedinUrl'],
    },
    'report.recruiterProficiency': {
        entity: 'report',
        properties: ['recruiterProficiency'],
    },
    'application.rejectionReason': {
        entity: 'application',
        properties: ['rejectionReasonCode', 'rejectionNote'],
    },
    'application.importedDetails': {
        entity: 'application',
        properties: ['importedDetails'],
    },
};
/**
 * The §L23 object each restriction hangs off, so an HR Lead editing a role's field
 * rules (F2.09) edits them on the object row they belong to rather than in a
 * parallel table that could disagree with it.
 */
exports.FIELD_RESTRICTION_OBJECTS = {
    'position.salaryBand': 'org_unit_position',
    'candidate.expectedSalary': 'candidate',
    'offer.amount': 'offer',
    'candidate.currentSalary': 'candidate',
    'candidate.doNotRehireReason': 'candidate',
    'candidate.nationalId': 'candidate',
    'candidate.contactDetails': 'candidate',
    'report.recruiterProficiency': 'reports_own',
    'application.rejectionReason': 'application',
    'application.importedDetails': 'application',
};
/** Every entity type the serialisation layer knows how to mask. */
exports.RESTRICTED_ENTITIES = Array.from(new Set(Object.values(exports.FIELD_RESTRICTION_TARGETS).flatMap((t) => [t.entity, ...(t.also ?? []).map((other) => other.entity)])));
exports.FIELD_RESTRICTIONS_BY_FIELD = new Map(exports.FIELD_RESTRICTIONS.map((r) => [r.field, r]));
/**
 * A restricted field's name in plain words, for every screen that names one (bug list #24).
 *
 * The catalogue's label wins. A field missing from it — a stale path saved on a role
 * before a rename — still reads as words ("candidate.doNotRehireReason" becomes
 * "Do not rehire reason"), never as a code.
 */
function fieldRestrictionLabel(field) {
    const label = exports.FIELD_RESTRICTIONS_BY_FIELD.get(field)?.label;
    if (label)
        return label;
    const property = field.slice(field.lastIndexOf('.') + 1);
    const words = property
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
}
/** The placeholder a restricted field renders as. Never an empty string (§L23, 06-qol §11). */
exports.RESTRICTED_FIELD_PLACEHOLDER = '—';
// --- Transition permissions (§L23) ------------------------------------------
exports.RESTRICTED_TRANSITIONS = [
    'skip_pipeline_stage',
    'override_decision_rule',
    'override_critical_criterion',
    'waive_missing_scorecard',
    'reopen_closed_requisition_or_job',
    'set_do_not_rehire',
    'merge_candidates',
    'unmerge_candidates',
    'manual_sla_pause',
    'cancel_job_with_active_candidates',
    'withdraw_sent_offer',
    'impersonate_user',
];
exports.RESTRICTED_TRANSITION_RULES = {
    skip_pipeline_stage: { permitted: ['admin', 'hr_lead'], requiresReason: true },
    override_decision_rule: { permitted: ['admin', 'hr_lead'], requiresReason: true },
    override_critical_criterion: { permitted: ['admin', 'hr_lead'], requiresReason: true },
    waive_missing_scorecard: { permitted: ['admin', 'hr_lead'], requiresReason: true },
    reopen_closed_requisition_or_job: { permitted: ['admin', 'hr_lead'] },
    set_do_not_rehire: { permitted: ['admin', 'hr_lead'] },
    merge_candidates: { permitted: ['admin', 'hr_lead', 'recruiter'] },
    unmerge_candidates: { permitted: ['admin', 'hr_lead'], note: 'Within 30 days of the merge (L2).' },
    manual_sla_pause: {
        permitted: ['admin', 'hr_lead', 'hiring_manager'],
        requiresReason: true,
        note: 'Hiring manager is capped at 5 working days (L7).',
    },
    cancel_job_with_active_candidates: { permitted: ['admin', 'hr_lead'], requiresReason: true },
    withdraw_sent_offer: {
        permitted: ['admin', 'hr_lead'],
        requiresReason: true,
        note: 'Requires written justification.',
    },
    impersonate_user: { permitted: ['admin'], note: 'Read-only, and always logged.' },
};
// --- Evaluation helpers -----------------------------------------------------
function cellFor(object, role) {
    return exports.PERMISSION_MATRIX[object][role];
}
/**
 * Cells the role builder may not lower on the standard role.
 *
 * Taking user management away from Admin locks every Admin out of the one
 * screen that could give it back (S0–S4 audit, item 6).
 */
const LOCKED_CELLS = {
    admin: ['user_management'],
};
exports.LOCKED_CELL_REASON = 'Always on. Admin must keep full user management, or nobody could manage users.';
function isLockedCell(role, object) {
    if (!role)
        return false;
    return LOCKED_CELLS[role]?.some((locked) => locked === object) ?? false;
}
/** Default deny: anything not explicitly granted is refused (CLAUDE.md §10). */
function canRead(object, roles) {
    return roles.some((role) => cellFor(object, role).level !== 'none');
}
function canWrite(object, roles) {
    return roles.some((role) => {
        const cell = cellFor(object, role);
        return cell.level !== 'none' && cell.level !== 'read' && cell.readOnly !== true;
    });
}
/** True when at least one of the viewer's roles may see this restricted field. */
function canSeeField(field, roles) {
    const restriction = exports.FIELD_RESTRICTIONS_BY_FIELD.get(field);
    if (!restriction)
        return true;
    return roles.some((role) => restriction.visibleTo.includes(role));
}
function canPerformRestrictedTransition(transition, roles) {
    return roles.some((role) => exports.RESTRICTED_TRANSITION_RULES[transition].permitted.includes(role));
}
/** Every field the given roles may not see — the set the serialiser masks. */
function hiddenFieldsFor(roles) {
    return exports.FIELD_RESTRICTIONS.filter((r) => !roles.some((role) => r.visibleTo.includes(role))).map((r) => r.field);
}
/** Guard for parsing an unknown string into a role. */
function isRoleKey(value) {
    return enums_1.ROLE_KEYS.includes(value);
}
/**
 * The restricted field paths one role may NOT see, grouped by the §L23 object they
 * belong to. This is what seeds `role_permissions.restricted_fields`, so the
 * database carries the same answer the matrix does and an HR Lead can then narrow
 * it further per role (F2.09) without editing code.
 */
function restrictedFieldsFor(role, object) {
    const fromMatrix = exports.PERMISSION_MATRIX[object][role].hiddenFields ?? [];
    const fromFieldRules = exports.FIELD_RESTRICTIONS.filter((r) => exports.FIELD_RESTRICTION_OBJECTS[r.field] === object && !r.visibleTo.includes(role)).map((r) => r.field);
    return Array.from(new Set([...fromMatrix, ...fromFieldRules]));
}
/**
 * The properties to mask on a node of `entity`, given the field paths hidden from
 * the viewer. Returned as a set because the serialiser tests every key of every
 * node against it.
 */
function maskedPropertiesFor(entity, hiddenFields) {
    const properties = new Set();
    for (const field of hiddenFields) {
        const target = exports.FIELD_RESTRICTION_TARGETS[field];
        if (!target)
            continue;
        for (const node of [target, ...(target.also ?? [])]) {
            if (node.entity !== entity)
                continue;
            for (const property of node.properties)
                properties.add(property);
        }
    }
    return properties;
}
//# sourceMappingURL=permissions.js.map